import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

import Home from './containers/Home';
import './styles/designSystem.css';

import './utils/axiosConfig';
import { AppContextProvider } from './Context/AppContextProvider';

import { Provider } from 'react-redux';
import store from './store';

import Layout from './hocs/Layout';
import FlowGuard from './flowGuard';
import MembershipRequired from './components/MembershipRequired';
import { DormantFallback, DormantFeature } from './components/DormantFeature';
import { ErrorBoundary } from './components/ErrorBoundary';
import { lazyWithRetry } from './utils/lazyWithRetry';
import AnalyticsRouteTracker from './components/AnalyticsRouteTracker';
import {
  loadAdminUsageDashboard,
  loadConnectHub,
  loadConnectPlaidRedirect,
  loadChatPage,
  loadDashboard,
  loadFinances,
  loadBudgets,
  loadTransactions,
  loadSubscriptions,
  loadSpending,
  loadCreateBudget,
  loadUserSurvey,
  loadSharedTripView,
  loadMyModel,
  loadCreatorDashboard,
  loadBudgetDetail,
  loadComingSoon,
  loadBudgetEdit,
  loadTrips,
  loadProfile,
  loadEditPreferences,
  loadSubscription,
  loadPlaidOAuthCallback,
  loadStravaOAuthCallback,
  loadOuraOAuthCallback,
  loadSpotifyOAuthCallback,
  loadGoogleCalendarOAuthCallback,
} from './SignedInScreens';

// The test branch is direct so Jest stays synchronous. Webpack drops it from
// the production graph, and the import() branch becomes a route chunk.
// Dashboard, chat, and finance stay registered below and still default off.
let About;
let ContactPage;
let LegalPage;
let PublicGuides;
let PublicGuideDetail;
let PublicCatalogPage;
let StaticDocumentPage;
let SharedLensApproval;
let Login;
let Signup;
let AffiliateRefRedirect;
let Activate;
let ResetPassword;
let ResetPasswordConfirm;
let OAuthCallback;
let StripeSuccess;
if (process.env.NODE_ENV === 'test') {
  About = require('./containers/About').default;
  ContactPage = require('./containers/Contact').default;
  LegalPage = require('./containers/LegalPage').default;
  PublicGuides = require('./containers/public/PublicGuides').default;
  PublicGuideDetail = require('./containers/public/PublicGuideDetail').default;
  PublicCatalogPage = require('./containers/public/PublicCatalogPage').default;
  StaticDocumentPage = require('./containers/public/StaticDocumentPage').default;
  SharedLensApproval = require('./containers/public/SharedLensApproval').default;
  Login = require('./containers/Login').default;
  Signup = require('./containers/Signup').default;
  AffiliateRefRedirect = require('./containers/AffiliateRefRedirect').default;
  Activate = require('./containers/Activate').default;
  ResetPassword = require('./containers/ResetPassword').default;
  ResetPasswordConfirm = require('./containers/ResetPasswordConfirm').default;
  OAuthCallback = require('./containers/OAuthCallback').default;
  StripeSuccess = require('./containers/StripeSuccess').default;
} else {
  About = lazyWithRetry(() => import(/* webpackChunkName: "about" */ './containers/About'));
  ContactPage = lazyWithRetry(() => import(/* webpackChunkName: "contact" */ './containers/Contact'));
  LegalPage = lazyWithRetry(() => import(/* webpackChunkName: "legal" */ './containers/LegalPage'));
  PublicGuides = lazyWithRetry(() => import(/* webpackChunkName: "guides" */ './containers/public/PublicGuides'));
  PublicGuideDetail = lazyWithRetry(() => import(/* webpackChunkName: "guides" */ './containers/public/PublicGuideDetail'));
  PublicCatalogPage = lazyWithRetry(() => import(/* webpackChunkName: "catalog" */ './containers/public/PublicCatalogPage'));
  StaticDocumentPage = lazyWithRetry(() => import(/* webpackChunkName: "static-doc" */ './containers/public/StaticDocumentPage'));
  SharedLensApproval = lazyWithRetry(() => import(/* webpackChunkName: "shared-lens" */ './containers/public/SharedLensApproval'));
  Login = lazyWithRetry(() => import(/* webpackChunkName: "login" */ './containers/Login'));
  Signup = lazyWithRetry(() => import(/* webpackChunkName: "signup" */ './containers/Signup'));
  AffiliateRefRedirect = lazyWithRetry(() => import(/* webpackChunkName: "affiliate" */ './containers/AffiliateRefRedirect'));
  Activate = lazyWithRetry(() => import(/* webpackChunkName: "activate" */ './containers/Activate'));
  ResetPassword = lazyWithRetry(() => import(/* webpackChunkName: "reset" */ './containers/ResetPassword'));
  ResetPasswordConfirm = lazyWithRetry(() => import(/* webpackChunkName: "reset" */ './containers/ResetPasswordConfirm'));
  OAuthCallback = lazyWithRetry(() => import(/* webpackChunkName: "auth-callback" */ './containers/OAuthCallback'));
  StripeSuccess = lazyWithRetry(() => import(/* webpackChunkName: "stripe-return" */ './containers/StripeSuccess'));
}

// Trips, including the Trip Project pay screen, loads on its own. Dashboard,
// chat, and finance stay registered in separate chunks. Flags still default off.
// The cascade stylesheet downloads beside the screen instead of before it.
const lazySignedIn = (load, install) =>
  lazyWithRetry(() =>
    Promise.all([
      load(),
      import(/* webpackChunkName: "signed-in-cascade" */ './signedInCascade'),
    ]).then(([mod, cascade]) => {
      install(cascade);
      if (!mod.default) {
        return Promise.reject(new Error('Signed-in screen is missing a default export.'));
      }
      return { default: mod.default };
    })
  );

const AdminUsageDashboard = lazySignedIn(loadAdminUsageDashboard, (cascade) => cascade.installAccountCascade());
const ConnectHub = lazySignedIn(loadConnectHub, (cascade) => cascade.installAccountCascade());
const ConnectPlaidRedirect = lazySignedIn(loadConnectPlaidRedirect, (cascade) => cascade.installAccountCascade());
const ChatPage = lazySignedIn(loadChatPage, (cascade) => cascade.installAccountCascade());
const Dashboard = lazySignedIn(loadDashboard, (cascade) => cascade.installAccountCascade());
const Finances = lazySignedIn(loadFinances, (cascade) => cascade.installAccountCascade());
const Budgets = lazySignedIn(loadBudgets, (cascade) => cascade.installAccountCascade());
const Transactions = lazySignedIn(loadTransactions, (cascade) => cascade.installAccountCascade());
const Subscriptions = lazySignedIn(loadSubscriptions, (cascade) => cascade.installAccountCascade());
const Spending = lazySignedIn(loadSpending, (cascade) => cascade.installAccountCascade());
const CreateBudget = lazySignedIn(loadCreateBudget, (cascade) => cascade.installAccountCascade());
// Survey does not use the signed-in cascade sheets. Loading them here pulls
// profile/admin CSS and a Google font import onto the conversion intro.
const UserSurvey = lazyWithRetry(() =>
  loadUserSurvey().then((mod) => {
    if (!mod.default) {
      return Promise.reject(new Error('Signed-in screen is missing a default export.'));
    }
    return { default: mod.default };
  })
);
const SharedTripView = lazySignedIn(loadSharedTripView, (cascade) => cascade.installAccountCascade());
const MyModel = lazySignedIn(loadMyModel, (cascade) => cascade.installAccountCascade());
const CreatorDashboard = lazySignedIn(loadCreatorDashboard, (cascade) => cascade.installAccountCascade());
const BudgetDetail = lazySignedIn(loadBudgetDetail, (cascade) => cascade.installAccountCascade());
const ComingSoon = lazySignedIn(loadComingSoon, (cascade) => cascade.installAccountCascade());
const BudgetEdit = lazySignedIn(loadBudgetEdit, (cascade) => cascade.installAccountCascade());
const Trips = lazySignedIn(loadTrips, (cascade) => cascade.installTripsCascade());
const Profile = lazySignedIn(loadProfile, (cascade) => cascade.installAccountCascade());
const EditPreferences = lazySignedIn(loadEditPreferences, (cascade) => cascade.installAccountCascade());
const Subscription = lazySignedIn(loadSubscription, (cascade) => cascade.installAccountCascade());
const PlaidOAuthCallback = lazySignedIn(loadPlaidOAuthCallback, (cascade) => cascade.installAccountCascade());
const StravaOAuthCallback = lazySignedIn(loadStravaOAuthCallback, (cascade) => cascade.installAccountCascade());
const OuraOAuthCallback = lazySignedIn(loadOuraOAuthCallback, (cascade) => cascade.installAccountCascade());
const SpotifyOAuthCallback = lazySignedIn(loadSpotifyOAuthCallback, (cascade) => cascade.installAccountCascade());
const GoogleCalendarOAuthCallback = lazySignedIn(loadGoogleCalendarOAuthCallback, (cascade) => cascade.installAccountCascade());
const TripOptionsPreview =
  process.env.NODE_ENV !== 'production'
    ? lazy(() => import('./containers/public/TripOptionsPreview'))
    : null;

function RouteChunkFallback() {
  return (
    <div className="flow-guard-loading" role="status" aria-live="polite">
      <div className="flow-guard-loading__spinner" aria-hidden="true" />
      <p className="flow-guard-loading__text">Opening...</p>
    </div>
  );
}

const App = () => (
  <Provider store={store}>
    <Router>
      <AnalyticsRouteTracker />
      <ErrorBoundary>
      <Layout>
        <Suspense fallback={<RouteChunkFallback />}>
        <Routes>

          {/* ------------------ */}
          {/* Public Marketing */}
          {/* These routes are never redirected by dormant feature flags. */}
          {/* ------------------ */}
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<LegalPage documentType="privacy" />} />
          <Route path="/terms" element={<LegalPage documentType="terms" />} />
          <Route path="/guides" element={<PublicGuides />} />
          <Route path="/guides/:slug" element={<PublicGuideDetail />} />
          <Route path="/shared/trips/:token" element={<SharedTripView />} />
          <Route path="/shared/lenses/:token" element={<SharedLensApproval />} />
          {/* Dev-only fixture preview; never routed in production builds. */}
          {process.env.NODE_ENV !== "production" && (
            <Route path="/dev/trip-options" element={<TripOptionsPreview />} />
          )}

          {/* ------------------ */}
          {/* Auth (Public) */}
          {/* ------------------ */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/r/:code" element={<AffiliateRefRedirect />} />
          <Route path="/r/:code/" element={<AffiliateRefRedirect />} />
          <Route path="/c/:code" element={<AffiliateRefRedirect />} />
          <Route path="/c/:code/" element={<AffiliateRefRedirect />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/password/reset/confirm/:uid/:token" element={<ResetPasswordConfirm />} />
          <Route path="/activate/:uid/:token" element={<Activate />} />
          <Route path="/oauth/callback" element={<OAuthCallback />} />
          <Route path="/stripe/success" element={<StripeSuccess />} />

          {/* Static files. A client-side visit loads the file instead of an empty shell. */}
          <Route path="/samples" element={<StaticDocumentPage />} />
          <Route path="/samples/*" element={<StaticDocumentPage />} />
          <Route path="/llms.txt" element={<StaticDocumentPage />} />
          <Route path="/llms-full.txt" element={<StaticDocumentPage />} />
          <Route path="/robots.txt" element={<StaticDocumentPage />} />
          <Route path="/sitemap.xml" element={<StaticDocumentPage />} />
          <Route path="/terms-of-service.pdf" element={<StaticDocumentPage />} />
          <Route path="/privacy-policy.pdf" element={<StaticDocumentPage />} />
          <Route path="/compare/dople-vs-mindtrip" element={<StaticDocumentPage />} />

          {/* ------------------ */}
          {/* Protected App */}
          {/* ------------------ */}
          <Route
            path="/*"
            element={
              <FlowGuard>
                <AppContextProvider>
                <Routes>

                  {/* Catalog pages that have a React body. Unknown slugs use the
                      not-found page. Static files are routed above. */}
                  <Route path="/compare" element={<PublicCatalogPage kind="compare" />} />
                  <Route path="/compare/:slug" element={<PublicCatalogPage kind="compare" />} />
                  <Route path="/use-cases" element={<PublicCatalogPage kind="use-cases" />} />
                  <Route path="/use-cases/:slug" element={<PublicCatalogPage kind="use-cases" />} />

                  {/* Onboarding */}
                  <Route path="/survey" element={<UserSurvey />} />
                  <Route path="/connect" element={<ConnectHub />} />
                  <Route path="/link" element={<ConnectPlaidRedirect />} />
                  <Route path="/create-model" element={<Navigate to="/trips" replace />} />
                  <Route path="/subscribe" element={<Subscription />} />
                  <Route path="/trips" element={<Trips />} />
                  <Route path="/trips/create" element={<Trips />} />
                  <Route path="/trips/building" element={<Trips />} />
                  <Route path="/trips/:tripId" element={<Trips />} />

                  <Route path="/admin/usage" element={<AdminUsageDashboard />} />
                  <Route path="/admin/creators" element={<CreatorDashboard admin />} />
                  <Route path="/creator" element={<CreatorDashboard />} />

                  {/* Dashboard, chat, and finance stay registered. Flags default off and send those URLs to Trips. */}
                  <Route
                    path="/dashboard"
                    element={
                      <DormantFeature feature="dashboard">
                        <MembershipRequired surface="dashboard">
                          <Dashboard />
                        </MembershipRequired>
                      </DormantFeature>
                    }
                  />
                  <Route
                    path="/dashboard/comingsoon"
                    element={
                      <DormantFeature feature="dashboard">
                        <ComingSoon />
                      </DormantFeature>
                    }
                  />
                  <Route
                    path="/dashboard/finances"
                    element={
                      <DormantFeature feature="finance">
                        <MembershipRequired surface="money">
                          <Finances />
                        </MembershipRequired>
                      </DormantFeature>
                    }
                  />
                  <Route
                    path="/dashboard/budgets"
                    element={
                      <DormantFeature feature="finance">
                        <MembershipRequired surface="money">
                          <Budgets />
                        </MembershipRequired>
                      </DormantFeature>
                    }
                  />
                  <Route
                    path="/dashboard/transactions"
                    element={
                      <DormantFeature feature="finance">
                        <MembershipRequired surface="money">
                          <Transactions />
                        </MembershipRequired>
                      </DormantFeature>
                    }
                  />
                  <Route
                    path="/dashboard/subscriptions"
                    element={
                      <DormantFeature feature="finance">
                        <MembershipRequired surface="money">
                          <Subscriptions />
                        </MembershipRequired>
                      </DormantFeature>
                    }
                  />
                  <Route path="/dashboard/loans" element={<Navigate to="/dashboard/subscriptions" replace />} />
                  <Route
                    path="/dashboard/spending"
                    element={
                      <DormantFeature feature="finance">
                        <MembershipRequired surface="money">
                          <Spending />
                        </MembershipRequired>
                      </DormantFeature>
                    }
                  />
                  <Route path="/dashboard/goals" element={<Navigate to="/trips" replace />} />
                  <Route
                    path="/dashboard/mymodel"
                    element={
                      <DormantFeature feature="dashboard">
                        <MyModel />
                      </DormantFeature>
                    }
                  />
                  <Route path="/dashboard/*" element={<DormantFallback feature="dashboard" />} />

                  <Route
                    path="/budget/create"
                    element={
                      <DormantFeature feature="finance">
                        <MembershipRequired surface="money">
                          <CreateBudget />
                        </MembershipRequired>
                      </DormantFeature>
                    }
                  />
                  <Route
                    path="/budget/:budgetId"
                    element={
                      <DormantFeature feature="finance">
                        <MembershipRequired surface="money">
                          <BudgetDetail />
                        </MembershipRequired>
                      </DormantFeature>
                    }
                  />
                  <Route
                    path="/budget/:budgetId/edit"
                    element={
                      <DormantFeature feature="finance">
                        <MembershipRequired surface="money">
                          <BudgetEdit />
                        </MembershipRequired>
                      </DormantFeature>
                    }
                  />
                  <Route path="/budget" element={<DormantFallback feature="finance" />} />
                  <Route path="/budget/*" element={<DormantFallback feature="finance" />} />

                  {/* User */}
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/profile/preferences" element={<EditPreferences />} />

                  <Route
                    path="/chat"
                    element={
                      <DormantFeature feature="chat">
                        <MembershipRequired surface="chat">
                          <ChatPage />
                        </MembershipRequired>
                      </DormantFeature>
                    }
                  />
                  <Route path="/chat/*" element={<DormantFallback feature="chat" />} />

                  {/* OAuth Callbacks */}
                  <Route path="/plaid/callback" element={<PlaidOAuthCallback />} />
                  <Route path="/strava/callback" element={<StravaOAuthCallback />} />
                  <Route path="/oura/callback" element={<OuraOAuthCallback />} />
                  <Route path="/spotify/callback" element={<SpotifyOAuthCallback />} />
                  <Route path="/google-calendar/callback" element={<GoogleCalendarOAuthCallback />} />

                </Routes>
                </AppContextProvider>
              </FlowGuard>
            }
          />

        </Routes>
        </Suspense>
      </Layout>
      </ErrorBoundary>
    </Router>
  </Provider>
);

export default App;
